import React, { FC, useMemo, useState } from 'react';
import { WebsiteHeaderLayout_VideoBackground } from '@apps/guest/websiteHeader';
import { Box, Flex, useToast } from '@withjoy/joykit';
import { PHOTO_PRESENTATION_CONFIG } from '@shared/utils/photoPresentationLayout';
import { AnimatedImageBox } from '@apps/guest/packages/layout-engine/widgets/Custom/components/PhotoTrio/PhotoTrio.styles';
import { EventNameNavForStandaloneHeader, EventNameNavForStandaloneHeaderProps } from '../components/EventNameNavForStandaloneHeader/EventNameNavForStandaloneHeader';
import { BrannanNavigation, EventPageType } from '@graphql/generated';
import { pxToRem } from '@withjoy/joykit/theme';
import { sendMessageToParentWindow } from '@shared/utils/previewMessageBus';
import { useEventCallback } from '@shared/utils/hooks/useEventCallback';
import { GuestSiteEventDesign } from '../../../../layout.types';
import { addError } from '@shared/utils/logger';
import { useIsMobileScreen } from '@shared/utils/media/useMediaScreens';
import { extractVimeoVideoId, extractYoutubeVideoId, isVimeoUrl, isYoutubeUrl } from '@shared/utils/video';
import { useTranslation } from '@shared/core';
import { isInIframe } from '@shared/utils/isInIframe';

export type VideoBackgroundLayoutProps = Readonly<
  {
    websiteHeader: WebsiteHeaderLayout_VideoBackground;
    showSkeleton?: boolean;
    showPlaceholder?: boolean;
    currentPageType: EventPageType | undefined;
    eventDesign: GuestSiteEventDesign;
    isInlineEditing?: boolean;
    renderedContainerHeight?: number;
    renderedContainerWidth?: number;
    editableContentRef?: React.RefObject<HTMLDivElement>;
    forceRenderViewIn?: 'mobile' | 'desktop';
    scaleValue?: number;
  } & EventNameNavForStandaloneHeaderProps
>;

type VideoBackgroundRendererProps = Readonly<{
  videoUrl: string;
  thumbnailUrl?: string;
  height: string;
  isCompactView: boolean;
  children: React.ReactNode;
}>;

const CONTAINER_ASPECT_RATIO = PHOTO_PRESENTATION_CONFIG.videoBackground.aspectRatio as number;

const VideoBackgroundRenderer: FC<VideoBackgroundRendererProps> = props => {
  const { tq } = useTranslation('videoBackground');
  const { videoUrl, thumbnailUrl, height, isCompactView, children } = props;
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(false);
  const isPreviewing = isInIframe();
  const { toast } = useToast();

  const handleIframeLoad = useEventCallback(() => {
    setIsLoading(false);
  });

  const handleIframeError = useEventCallback(() => {
    setIsLoading(false);
    setError(true);
  });

  const preparedVideoUrl = useMemo(() => {
    try {
      if (isVimeoUrl(videoUrl)) {
        const searchParams = new URL(videoUrl).searchParams;

        [
          ['background', '1'],
          ['autoplay', '1'],
          ['loop', '1'],
          ['muted', '1'],
          ['playsinline', '1'],
        ].forEach(([key, value]) => {
          if (searchParams.has(key)) {
            return;
          }

          searchParams.set(key, value);
        });

        return `https://player.vimeo.com/video/${extractVimeoVideoId(videoUrl)}?${searchParams.toString()}`;
      }
      if (isYoutubeUrl(videoUrl)) {
        const searchParams = new URL(videoUrl).searchParams;

        [
          ['autoplay', '1'],
          ['mute', '1'],
          ['controls', '0'],
          ['loop', '1'],
          ['playlist', extractYoutubeVideoId(videoUrl) ?? ''],
          ['playsinline', '1'],
          ['modestbranding', '1'],
          ['rel', '0'],
        ].forEach(([key, value]) => {
          if (searchParams.has(key)) {
            return;
          }

          searchParams.set(key, value);
        });

        return `https://www.youtube.com/embed/${extractYoutubeVideoId(videoUrl)}?${searchParams.toString()}`;
      }

      return videoUrl;
    } catch (error) {
      if (isPreviewing) {
        toast(tq`We couldn't load your video background.`);
      }

      addError(error, { context: 'VideoBackgroundRenderer#preparedVideoUrl' });
      return '';
    }
  }, [videoUrl, isPreviewing, toast, tq]);

  if (!preparedVideoUrl) {
    return (
      <AnimatedImageBox
        flexDirection="column"
        alignItems="center"
        width="100%"
        height={height}
        aspectRatio={isCompactView ? 'unset' : `${CONTAINER_ASPECT_RATIO}`}
        overflow="hidden"
        backgroundColor="lightgray"
        minHeight={pxToRem(96)}
      >
        <Box
          width="100%"
          height="100%"
          position={isCompactView ? 'relative' : 'absolute'}
          zIndex={1}
        >
          {children}
        </Box>
      </AnimatedImageBox>
    );
  }

  return (
    <AnimatedImageBox
      flexDirection="column"
      alignItems="center"
      width="100%"
      height={height}
      aspectRatio={isCompactView ? 'unset' : `${CONTAINER_ASPECT_RATIO}`}
      overflow="hidden"
      backgroundColor="lightgray"
      minHeight={pxToRem(96)}
    >
      <Box
        as="iframe"
        title={tq`Video Background`}
        src={preparedVideoUrl}
        allow="autoplay; fullscreen; picture-in-picture"
        position="absolute"
        top="50%"
        left="50%"
        border="none"
        outline="none"
        transform="translate(-50%, -50%)"
        opacity={isLoading || error ? 0 : 1}
        transition="opacity 0.3s ease-in-out"
        onLoad={handleIframeLoad}
        onError={handleIframeError}
        width="100vw"
        height="100vh"
        minWidth="177.78vh"
        minHeight="56.25vw"
      />
      {(isLoading || error) && thumbnailUrl && (
        <Box
          as="img"
          src={thumbnailUrl}
          alt={tq`Video Background Thumbnail`}
          position="absolute"
          top={0}
          left={0}
          width="100%"
          height="100%"
          objectFit="cover"
          zIndex={1}
        />
      )}
      <Box
        width="100%"
        height="100%"
        position={isCompactView ? 'relative' : 'absolute'}
        zIndex={1}
      >
        {children}
      </Box>
    </AnimatedImageBox>
  );
};

export const VideoBackgroundLayout: FC<VideoBackgroundLayoutProps> = props => {
  const {
    websiteHeader,
    showSkeleton,
    showPlaceholder = false,
    currentPageType,
    isInlineEditing = false,
    renderedContainerHeight,
    renderedContainerWidth,
    editableContentRef,
    forceRenderViewIn,
    scaleValue = 1,
    pages,
    pageSlug,
    ...restProps
  } = props;

  const pageIndex = useMemo(() => pages?.findIndex(p => p.pageSlug === pageSlug), [pages, pageSlug]);
  // compact view is only for brannan classic navigation layout
  const isCompactView = restProps.eventDesign?.brannanNavigation === BrannanNavigation.classic && currentPageType !== EventPageType.welcome && pageIndex !== 0;

  //  Hide video background in compact view when navbar is in menu variant
  // The videobackground is not needed for the menu variant as it is a hamburger menu
  // The hamburger menu is shown only in mobile devices
  const shouldHideVideoBackground = useMemo(() => {
    return isCompactView && restProps.navBarVariant === 'menu';
  }, [isCompactView, restProps.navBarVariant]);

  const { height, aspectRatio } = useMemo(
    () =>
      isCompactView
        ? { height: '100%', aspectRatio: 'unset' }
        : { height: isInlineEditing && renderedContainerHeight ? `${renderedContainerHeight}px` : '100vh', aspectRatio: `${CONTAINER_ASPECT_RATIO}` },
    [isCompactView, isInlineEditing, renderedContainerHeight],
  );

  const isMobile = useIsMobileScreen();
  const { desktopVideoUrl, mobileVideoUrl } = websiteHeader.headerPhoto;
  const videoUrl = isMobile && mobileVideoUrl ? mobileVideoUrl : desktopVideoUrl;

  // Memoize container dimensions to prevent unnecessary style recalculations
  // when hiding video background on mobile/tablet
  const { containerAspectRatio, containerHeight, containerMinHeight, containerOverflow } = useMemo(() => {
    return {
      containerAspectRatio: shouldHideVideoBackground ? 'unset' : aspectRatio,
      containerHeight: shouldHideVideoBackground ? 'auto' : height,
      containerMinHeight: shouldHideVideoBackground ? 'auto' : pxToRem(96),
      containerOverflow: shouldHideVideoBackground ? 'visible' : 'hidden',
    };
  }, [shouldHideVideoBackground, aspectRatio, height]);

  const handleEditTextClick = useEventCallback(() => {
    // send the message to the parent window to open the text editor
    sendMessageToParentWindow({
      action: 'inlineEditingInteraction',
      source: 'joyPreview',
      value: {
        action: 'editWebsiteHeaderCustomTitle',
        inlineEditData: { data: props, validationConstraints: { maxLength: 30 } },
      },
    });
  });

  return (
    <Flex
      position="relative"
      zIndex={10}
      width="100%"
      aspectRatio={containerAspectRatio}
      height={containerHeight}
      minHeight={containerMinHeight}
      overflow={containerOverflow}
    >
      {shouldHideVideoBackground ? (
        <EventNameNavForStandaloneHeader
          eventDisplayName={websiteHeader.eventDisplayName}
          colors={websiteHeader.headerPhoto.colors}
          headerTextData={websiteHeader.headerPhoto.eventName}
          isCompactView={isCompactView}
          currentPageType={currentPageType}
          handleEditTextClick={handleEditTextClick}
          isContentEditable={isInlineEditing}
          editableContentRef={editableContentRef}
          forceRenderViewIn={forceRenderViewIn}
          scaleValue={scaleValue}
          pages={pages}
          pageSlug={pageSlug}
          renderedContainerHeight={renderedContainerHeight}
          renderedContainerWidth={renderedContainerWidth}
          {...restProps}
        />
      ) : (
        <VideoBackgroundRenderer
          videoUrl={videoUrl}
          thumbnailUrl={websiteHeader.headerPhoto.thumbnailImageUrl}
          height={height}
          isCompactView={isCompactView}
        >
          <EventNameNavForStandaloneHeader
            eventDisplayName={websiteHeader.eventDisplayName}
            colors={websiteHeader.headerPhoto.colors}
            headerTextData={websiteHeader.headerPhoto.eventName}
            isCompactView={isCompactView}
            currentPageType={currentPageType}
            handleEditTextClick={handleEditTextClick}
            isContentEditable={isInlineEditing}
            editableContentRef={editableContentRef}
            forceRenderViewIn={forceRenderViewIn}
            scaleValue={scaleValue}
            pages={pages}
            pageSlug={pageSlug}
            renderedContainerHeight={renderedContainerHeight}
            renderedContainerWidth={renderedContainerWidth}
            {...restProps}
          />
        </VideoBackgroundRenderer>
      )}
    </Flex>
  );
};

VideoBackgroundLayout.displayName = 'VideoBackgroundLayout';
